import { UATConstants, HttpVerbs } from '../Common/Models/UATConstants';
import { ServiceFactoryClientExecuteApi } from '../Common/Services/ServiceFactoryToExecuteApi';
import ErrorResponse from '../Models/ErrorResponse';
import { IDropdownOption } from '@fluentui/react';
import { appInsights } from 'src/Services/Insights';
import { IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';

export class DropdownService {
  private static isFetching: boolean = false;
  private static cachedResponse: any = null;
  private static pendingCallbacks: Array<{
    onSuccess: (response: any) => void;
    onError?: (error: ErrorResponse) => void;
  }> = [];

  private static cloneResponse<T>(response: T): T {
    return structuredClone(response);
  }

  public static fetchDropdownData(
    onRequestSuccess: (response: any) => void,
    onRequestError?: (error: ErrorResponse) => void
  ): any {
    // If we already have a cached response, return a deep clone so consumers can mutate freely
    if (this.cachedResponse) {
      onRequestSuccess(this.cloneResponse(this.cachedResponse));
      return;
    }

    // Queue this caller's callbacks
    this.pendingCallbacks.push({ onSuccess: onRequestSuccess, onError: onRequestError });

    // If a fetch is already in progress, don't make another API call
    if (this.isFetching) {
      return;
    }

    this.isFetching = true;
    const url = `${UATConstants.ApiPaths.GetDropdownData}`;
    ServiceFactoryClientExecuteApi.ExecuteRequestNoCache<any>(
      url,
      [],
      HttpVerbs.get,
      [],
      (response: any) => {
        // Cache the response so future calls never hit the API
        this.cachedResponse = this.cloneResponse(response);
        // Notify all queued callers with the response
        const callbacks = [...this.pendingCallbacks];
        this.pendingCallbacks = [];
        this.isFetching = false;
        callbacks.forEach((cb) => cb.onSuccess(this.cloneResponse(this.cachedResponse)));
      },
      (response: ErrorResponse) => {
        this.logError(response);
        // Notify all queued callers of the error
        const callbacks = [...this.pendingCallbacks];
        this.pendingCallbacks = [];
        this.isFetching = false;
        callbacks.forEach((cb) => {
          if (cb.onError) {
            cb.onError(response);
          }
        });
      },
      false
    );
  }

  public static logError = (errResponse: any) => {
    const exception: IExceptionTelemetry = {
      exception: errResponse,
      severityLevel: SeverityLevel.Error,
      properties: {
        name: 'DropdownsLoadError',
        url: UATConstants.ApiPaths.GetDropdownData,
        userName: sessionStorage.getItem('jwtUserAccountName'),
        userAlias: sessionStorage.getItem('jwtUserAccountEmail'),
      },
    };
    appInsights.trackException(exception);
  };

  public static GenerateDropdownOption<T>(
    values: T[],
    type: string,
    optional: string | { key: string; text: string },
    isNeeded: boolean
  ): IDropdownOption[] {
    const options: IDropdownOption[] = [];
    if (values && values.length > 0) {
      if (optional) {
        if (typeof optional === 'string') {
          options.push({ key: isNeeded ? 0 : optional, text: optional });
        } else {
          options.push({ key: optional.key, text: optional.text });
        }
      }

      values.forEach((value) => {
        options.push({
          key: isNeeded ? value['id'] : value[type],
          text: value[type],
        });
      });
    }
    return options;
  }

  public static GenerateAreaDropdownOption<T>(
    values: T[],
    type: string,
    optional: string,
    idNeeded: boolean
  ): IDropdownOption[] {
    const options: IDropdownOption[] = [];
    if (values && values.length > 0) {
      options.push({ key: idNeeded ? 0 : optional, text: optional });
      values.forEach((value) => {
        options.push({
          key: idNeeded ? value['id'] : value[type],
          text: value[type],
          id: value['ID'],
        });
      });
    }
    return options;
  }

  public static GenerateMASSSUDropdownOption<T>(
    values: T[],
    type: string,
    optional: string,
    idNeeded: boolean
  ): IDropdownOption[] {
    const options: IDropdownOption[] = [];
    if (values && values.length > 0) {
      options.push({ key: idNeeded ? 0 : optional, text: optional });
      values.forEach((value) => {
        options.push({
          key: idNeeded ? value['id'] : value[type],
          text: value[type],
          id: value['ID'],
          MaaS_SegmentAreaIds: value['MaaS_SegmentAreaIds'],
          MaaS_SegCommonRequestType: value['MaaS_SegCommonRequestType'],
        });
      });
    }
    return options;
  }

  public static GenerateMASSOUDropdownOption<T>(
    values: T[],
    type: string,
    optional: string,
    idNeeded: boolean
  ): IDropdownOption[] {
    const options: IDropdownOption[] = [];
    if (values && values.length > 0) {
      options.push({ key: idNeeded ? 0 : optional, text: optional });
      values.forEach((value) => {
        options.push({
          key: idNeeded ? value['id'] : value[type],
          text: value[type],
          MaaS_Segment_ID: value['MaaS_Segment_ID'],
          MaaS_CommonRequestType: value['MaaS_CommonRequestType'],
          MaaS_Area_ID: value['MaaS_Area_ID'],
        });
      });
    }
    return options;
  }

  public static GenerateReferralIDDropdownOption<T>(
    values: T[],
    type: string,
    optional: string,
    referralname: string
  ): IDropdownOption[] {
    const options: IDropdownOption[] = [];
    if (values && values.length > 0) {
      options.push({ key: referralname ? 0 : optional, text: optional });
      values.forEach((value) => {
        options.push({
          key: value[type],
          text: value[type] + ' ' + '|' + ' ' + value[referralname],
        });
      });
    }
    return options;
  }
}
// Generated by GitHub Copilot | Updated on: 2026-05-15 | 1 lines modified | Replaced JSON.parse(JSON.stringify()) with structuredClone() for optimized object cloning
